import type { ComponentType, JSX, PropsWithChildren } from 'react';

import { TrackingContext } from '@change-corgi/core/react/tracking';

import { useCurrentUserId } from 'src/app/shared/hooks/session';
import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { StarterDashboardCommentsPageCombinedState, StarterDashboardCommentsPrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { StarterDashboardCommentsPageDataContextProvider } from './pageData';
import { StarterDashboardCommentsPagePaginatedDataContextProvider } from './pagePaginatedData';

export type ErrorHandlerProps = { context: ErrorStateDetails<StarterDashboardCommentsPageCombinedState> };

type Props = {
	slug: string;
	prefetchedData: StarterDashboardCommentsPrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export function StarterDashboardCommentsPageContextProvider({
	children,
	slug,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}: PropsWithChildren<Props>): JSX.Element | null {
	const pageContextState = usePageContext(slug, prefetchedData);
	const userId = useCurrentUserId();

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData, pagePaginatedData } = pageContextState;

	return (
		<StarterDashboardCommentsPageDataContextProvider pageData={pageData}>
			<StarterDashboardCommentsPagePaginatedDataContextProvider pagePaginatedData={pagePaginatedData}>
				<TrackingContext
					properties={{
						petition_id: pageData.data.petition.id,
						own_petition: pageData.data.petition.user.id === userId,
					}}
				>
					{children}
				</TrackingContext>
			</StarterDashboardCommentsPagePaginatedDataContextProvider>
		</StarterDashboardCommentsPageDataContextProvider>
	);
}
